import type { SvgIconComponent } from '@mui/icons-material';
import type { InputHTMLAttributes } from 'react';
import type { FieldValues } from 'react-hook-form';
import { InputLabel, styled, Typography } from '@mui/material';
import { FilterInputWrapper as Wrapper } from './FilterInputWrapper';
import { StyledFilterInput as StyledNumberInput } from './StyledFilterInput';

const NumberInputWrapper = styled('div')(() => ({
  display: 'flex',
  flex: 1,
  height: '100%',
  width: '100%',
  alignItems: 'center',
  paddingLeft: '8px',
  paddingRight: '8px',

  '& input': {
    width: '100%',
    padding: '10px',
    height: '100%',
    outline: 'none',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
  },
}));

interface FilterNumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string | number;
  label: string;
  error?: FieldValues['errors'];
  disabled?: boolean;
  layout?: 'full' | 'half';
  icon?: SvgIconComponent;
  onChange?: (value: number | null) => void;
}

export const FilterNumberInput = ({ value, label, error, disabled, layout = 'full', icon: Icon, onChange, ...rest }: FilterNumberInputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      onChange?.(null);
      return;
    }

    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue)) {
      onChange?.(numericValue);
    }
  };

  return (
    <Wrapper layout={layout}>
      <InputLabel>{label}</InputLabel>

      <StyledNumberInput hasError={!!error}>
        <NumberInputWrapper>
          {Icon && <Icon className="text-primary mr-2" />}
          <input {...rest} type="number" value={value || ''} onChange={handleChange} disabled={disabled} />
        </NumberInputWrapper>
      </StyledNumberInput>

      {error && (
        <Typography variant="body1" color="error">
          {error.message}
        </Typography>
      )}
    </Wrapper>
  );
};
